import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, CheckCircle2, Hourglass, MapPin, MonitorSmartphone, Wallet, XCircle } from 'lucide-react';
import { Badge } from '../components/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Progress } from '../components/Progress';
import { StatTile } from '../components/StatTile';
import { InviteExpertsDialog } from '../components/InviteExpertsDialog';
import { InvitationList } from '../components/InvitationList';
import { useEvents } from '../contexts/EventsContext';
import { formatAED, formatDate } from '../utils/expert';
import { daysUntil, summarizeInvitations } from '../utils/event';
export function EventDetail() {
  const {
    id
  } = useParams();
  const {
    events
  } = useEvents();
  const event = events.find(e => e.id === id);
  if (!event) {
    return <main className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center" data-id="element-708" data-mp="316q7ev6gdf81">
        <p className="text-sm font-medium" data-id="element-709" data-mp="21vk72z625c9u">Event not found</p>
        <Link to="/events" className="mt-4 inline-flex h-7 items-center rounded-md border px-3 text-xs font-medium hover:bg-muted" data-id="element-710" data-mp="21tzaz543hl9v">
          Back to events
        </Link>
      </main>;
  }
  const s = summarizeInvitations(event);
  const days = daysUntil(event.date);
  const budgetUsed = event.budget ? Math.min(100, s.committedFees / event.budget * 100) : 0;
  const overBudget = event.budget > 0 && s.committedFees > event.budget;
  return <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-id="element-711" data-mp="1dywk6rlqd7qr">
      <Link to="/events" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-id="element-712" data-mp="1rxwcc98gc4ro">
        <ArrowLeft className="size-3.5" aria-hidden="true" data-id="element-713" data-mp="1myjq9qpen1q5" /> Events
      </Link>

      <section className="mt-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between" data-id="element-714" data-mp="33ct1rk8jsvzk">
        <div className="max-w-2xl" data-id="element-715" data-mp="2ye8cgfuu3wpw">
          <div className="flex flex-wrap items-center gap-2" data-id="element-716" data-mp="2yd6oxbwmthra">
            <h1 className="text-2xl font-semibold tracking-tight" data-id="element-717" data-mp="1w0tz1qf27mce">{event.name}</h1>
            <Badge variant="secondary" data-id="element-718" data-mp="1w2wreh3oprka">{days >= 0 ? `In ${days} days` : 'Completed'}</Badge>
          </div>
          {event.description && <p className="mt-1.5 text-sm text-muted-foreground" data-id="element-719" data-mp="250qgb6z4dixf">{event.description}</p>}
          <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-id="element-720" data-mp="2a033xtxteu12">
            <span className="inline-flex items-center gap-1" data-id="element-721" data-mp="1dam25q1yds4n"><CalendarDays className="size-3" aria-hidden="true" data-id="element-722" data-mp="1dbx6ps3833wt" />{formatDate(event.date)}</span>
            <span className="inline-flex items-center gap-1" data-id="element-723" data-mp="1hbxj776rh518"><MapPin className="size-3" aria-hidden="true" data-id="element-724" data-mp="1mb0mam36l16k" />{event.location}</span>
            <span className="inline-flex items-center gap-1" data-id="element-725" data-mp="1r1h1obqkawvk"><MonitorSmartphone className="size-3" aria-hidden="true" data-id="element-726" data-mp="1r290dvfghaq2" />{event.format}</span>
            <Badge variant="outline" data-id="element-727" data-mp="tl1xmrswf62f">{event.category}</Badge>
          </p>
        </div>
        <InviteExpertsDialog event={event} data-id="element-728" data-mp="ykeij707m32d" />
      </section>

      <section aria-label="Invitation summary" className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-729" data-mp="yitodiht4m51">
        <StatTile label="Accepted" value={String(s.accepted)} icon={CheckCircle2} data-id="element-730" data-mp="m2kio6296z22" />
        <StatTile label="Awaiting reply" value={String(s.awaiting)} icon={Hourglass} data-id="element-731" data-mp="m0hhcpvnhji1" />
        <StatTile label="Declined" value={String(s.declined)} icon={XCircle} data-id="element-732" data-mp="h1o4mzo6twix" />
        <StatTile label="Committed fees" value={formatAED(s.committedFees)} hint="From accepted experts" icon={Wallet} data-id="element-733" data-mp="d145xrnyukuo" />
      </section>

      <div className="mt-8 grid gap-4 lg:grid-cols-[1fr_300px]" data-id="element-734" data-mp="84m6967htcp7">
        <section aria-labelledby="invited-heading" data-id="element-735" data-mp="3baz13yrqtwr">
          <h2 id="invited-heading" className="mb-3 text-sm font-semibold" data-id="element-736" data-mp="3ui478rrzbtas">Invited experts</h2>
          <InvitationList event={event} data-id="element-737" data-mp="3pjavp4w96cd9" />
        </section>

        <aside className="flex flex-col gap-4" data-id="element-738" data-mp="3pkvksezst33q">
          <Card data-id="element-739" data-mp="3lhfq3o7ahcto">
            <CardHeader data-id="element-740" data-mp="3lhz10kyiv0y7">
              <CardTitle data-id="element-741" data-mp="3lh6389hjjls7">Expert budget</CardTitle>
            </CardHeader>
            <CardContent data-id="element-742" data-mp="3gicpcfycdsri">
              {event.budget > 0 ? <>
                  <p className="text-2xl font-semibold tabular-nums" data-id="element-743" data-mp="1key75aid51aq">{formatAED(s.committedFees)}</p>
                  <p className="text-xs text-muted-foreground" data-id="element-744" data-mp="1ohl4fqxszefb">of {formatAED(event.budget)} committed</p>
                  <Progress value={budgetUsed} className="mt-3" aria-label="Budget used" data-id="element-745" data-mp="1aqg7wxpqf297" />
                  <p className={`mt-2 text-xs ${overBudget ? 'text-destructive' : 'text-muted-foreground'}`} data-id="element-746" data-mp="1fo7xvm6tgear">
                    {overBudget ? `Over budget by ${formatAED(s.committedFees - event.budget)}` : `${formatAED(event.budget - s.committedFees)} remaining`}
                  </p>
                </> : <p className="text-sm text-muted-foreground" data-id="element-747" data-mp="rt5bdkvhimxg">No budget set for this event.</p>}
            </CardContent>
          </Card>
          <Card data-id="element-748" data-mp="2m5v25tqk3g0m">
            <CardHeader data-id="element-749" data-mp="2m5czofdaw5k9">
              <CardTitle data-id="element-750" data-mp="2m2z8ihlqrdhz">How invitations work</CardTitle>
            </CardHeader>
            <CardContent data-id="element-751" data-mp="2r350xzzu5414">
              <ol className="space-y-3 text-xs text-muted-foreground" data-id="element-752" data-mp="317uylto7hlzj">
                <li data-id="element-753" data-mp="3183abi8lel95"><span className="font-medium text-foreground" data-id="element-754" data-mp="315qv3cu7yozs">1. Sent</span> — expert is notified by email, SMS and the BAK app.</li>
                <li data-id="element-755" data-mp="39zwwplasyq6n"><span className="font-medium text-foreground" data-id="element-756" data-mp="39zwy9pqgay8r">2. Viewed</span> — expert has opened the invitation.</li>
                <li data-id="element-757" data-mp="3596swci0yve2"><span className="font-medium text-foreground" data-id="element-758" data-mp="358drlr1u4y3b">3. Replied</span> — expert accepts or declines; you’re notified instantly.</li>
              </ol>
            </CardContent>
          </Card>
        </aside>
      </div>
    </main>;
}